import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { MotifTable, MotifTableCellRenderer, MotifTableHeaderRenderer } from '@ey-xd/motif-react';

import { EntityType, Position } from 'common/enums';

import './Table.scss';

type TableProps = {
    columnDefs: any;
    rowData?: any;
    frameworkComponents?: any;
    rowDragManaged?: boolean;
    rowHeight?: number;
    rowSelection?: string;
    suppressCellSelection?: boolean;
    suppressRowClickSelection?: boolean;
    suppressRowHoverHighlight?: boolean;
    noRowsOverlayComponent?: React.ReactNode;
    isNoRowsOverlayVisible?: boolean;
    noRowsTemplate?: string;
    overlayLoadingTemplate?: any;
    onSortChanged?: any;
    onRowSelected?: any;
    onMouseEnter?: any;
    onMouseLeave?: any;
    onRowDragEnd?: any;
    getRowClass?: any;
    preventColumnFit?: boolean;
    suppressDrag?: boolean;
    isScrollToTop?: boolean;
    resetScrollToTop?: () => void;
    fetchNextDataSet?: () => void;
    onGridReady?: (param: any) => void;
    onChangePage?: (page: number, limit: number) => void;
    onFilterChanged?: () => void;
    onSelectionChanged?: (value: any) => void;
    onPageSizeChange?: (value: any) => void;
    onGridSizeChanged?: (value: any) => void;
    onFirstDataRendered?: (value: any) => void;
};

const Table: FC<TableProps> = ({
    columnDefs,
    rowData,
    frameworkComponents,
    rowHeight = 40,
    isNoRowsOverlayVisible = false,
    noRowsTemplate,
    preventColumnFit,
    suppressDrag = false,
    isScrollToTop,
    fetchNextDataSet,
    onGridReady,
    ...rest
}: TableProps) => {
    const [firstRowIndex, setFirstRowIndex] = useState<number>(0);
    //TODO: create a type for the ag grid grid api
    const gridApiref = useRef<any>();
    const noRowsRef = useRef<boolean>(false);

    useEffect(() => {
        setTimeout(() => {
            if (firstRowIndex !== gridApiref.current?.getFirstDisplayedRow()) {
                gridApiref.current?.ensureIndexVisible(firstRowIndex, Position.Top);
            }
        }, 0);
    }, [rowData, firstRowIndex]);

    useEffect(() => {
        if (isScrollToTop) {
            gridApiref.current?.ensureIndexVisible(0, Position.Top);
            rest.resetScrollToTop?.();
            setFirstRowIndex(0);
        }
    }, [isScrollToTop, rest]);

    useEffect(() => {
        gridApiref.current?.setSuppressRowDrag(suppressDrag);
    }, [suppressDrag]);

    const toggleNoRowsOverlay = useCallback(() => {
        if (isNoRowsOverlayVisible) {
            setTimeout(() => {
                gridApiref.current?.showNoRowsOverlay();
            });
        } else {
            setTimeout(() => {
                gridApiref.current?.hideOverlay();
            });
        }
    }, [isNoRowsOverlayVisible]);

    useEffect(() => {
        noRowsRef.current = isNoRowsOverlayVisible;
        if (!gridApiref.current) return;
        toggleNoRowsOverlay();
    }, [isNoRowsOverlayVisible, toggleNoRowsOverlay, rowData]);

    const handleGridReady = (params: { [key: string]: string }) => {
        ({ api: gridApiref.current } = params);
        if (noRowsRef.current) {
            setTimeout(() => {
                gridApiref.current?.showNoRowsOverlay();
            });
        } else {
            setTimeout(() => {
                gridApiref.current?.hideOverlay();
            });
        }
        !preventColumnFit && gridApiref.current.sizeColumnsToFit();
        onGridReady?.(params);
    };

    const handleGridSizeChanged = () => {
        !preventColumnFit && gridApiref.current.sizeColumnsToFit();
    };

    const handleFirstDataRendered = () => {
        !preventColumnFit && gridApiref.current.sizeColumnsToFit();
    };

    const handleBodyScroll = () => {
        if (
            gridApiref.current?.getLastDisplayedRow() > 0 &&
            gridApiref.current?.getLastDisplayedRow() + 1 === rowData.length
        ) {
            fetchNextDataSet?.();
        }
    };

    const debouncedScrollHandler = (method: any, delay: any) => () => {
        setFirstRowIndex(gridApiref.current?.getFirstDisplayedRow());
        clearTimeout(method._tId);
        method._tId = setTimeout(function () {
            method();
        }, delay);
    };

    const getRowClass = (params: any) => {
        if (params.data.isExpanded) {
            return 'row-bg-light';
        } else if (params.data.entityType === EntityType.Indicator) {
            return 'row-bg-medium';
        } else if (params.data.entityType === EntityType.SubCategory) {
            return 'row-bg-light';
        } else {
            return 'row-bg-dark';
        }
    };

    const handleModelUpdated = (params: any) => {
        !preventColumnFit && params.api.sizeColumnsToFit();
    };

    const allFrameworkComponents = {
        agColumnHeader: MotifTableHeaderRenderer,
        motifTableCellRenderer: MotifTableCellRenderer,
        ...frameworkComponents
    };

    const defaultColDef = {
        headerClass: 'custom-header'
    };

    return (
        <MotifTable
            rowBuffer={0}
            rowData={rowData}
            rowHeight={rowHeight}
            defaultColDef={defaultColDef}
            columnDefs={columnDefs}
            frameworkComponents={allFrameworkComponents}
            suppressPaginationPanel={true}
            getRowClass={getRowClass}
            overlayNoRowsTemplate={noRowsTemplate}
            onGridReady={handleGridReady}
            onGridSizeChanged={handleGridSizeChanged}
            onFirstDataRendered={handleFirstDataRendered}
            onModelUpdated={handleModelUpdated}
            onBodyScroll={debouncedScrollHandler(handleBodyScroll, 1000)}
            {...rest}
        />
    );
};

export default Table;
